import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "./UserContext";
import { queryKeys } from "../graphql/queryKeys";
import { fetchLatestLegalAcceptance } from "../graphql/fetchers";
import { getCurrentLegalVersion } from "../utilities/legalDocuments";
import { recordLegalAcceptance } from "../utilities/legalAcceptance";
import {
  createLoginSessionId,
  getCurrentAuthToken,
  getStoredActiveSessionId,
} from "../utilities/loginEvents";

export type LegalGateStatus = "loading" | "required" | "accepted";

type LegalGateContextValue = {
  status: LegalGateStatus;
  currentVersion: string;
  acceptLegal: () => Promise<void>;
};

const LegalGateContext = createContext<LegalGateContextValue | null>(null);

export const LegalGateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isUserDataLoading, isUserActive, user } = useUser();
  const { i18n } = useTranslation("common");
  const queryClient = useQueryClient();

  const userId = user?.userId ?? null;
  const currentVersion = getCurrentLegalVersion(i18n.language);

  // Never query before auth + profile resolution completes, and skip entirely
  // for already-blocked inactive users (AuthGate blocks them regardless).
  const isQueryEnabled =
    isAuthenticated && !isUserDataLoading && isUserActive !== false && !!userId;

  const { data, isPending, isFetching } = useQuery({
    queryKey: queryKeys.legalAcceptance(userId ?? "anonymous"),
    queryFn: () => fetchLatestLegalAcceptance(userId as string),
    enabled: isQueryEnabled,
  });

  const status: LegalGateStatus = useMemo(() => {
    if (!isAuthenticated || isUserDataLoading) return "loading";
    if (!isQueryEnabled) {
      // isUserActive === false (or no userId yet): not gated, but not "accepted"
      // either — AuthGate already blocks these users before this matters.
      return "loading";
    }
    if (isPending || (isFetching && data === undefined)) return "loading";
    if (!data || data.version !== currentVersion) return "required";
    return "accepted";
  }, [
    isAuthenticated,
    isUserDataLoading,
    isQueryEnabled,
    isPending,
    isFetching,
    data,
    currentVersion,
  ]);

  const acceptLegal = useCallback(async () => {
    if (!userId) {
      throw new Error("Cannot accept legal documents without an authenticated user.");
    }

    const sessionId = getStoredActiveSessionId() ?? createLoginSessionId();
    const authToken = await getCurrentAuthToken();

    await recordLegalAcceptance({
      userId,
      version: currentVersion,
      sessionId,
      authToken,
    });

    // Update the cache synchronously (before this promise resolves) so AuthGate
    // sees "accepted" immediately and doesn't bounce back to /LegalAcceptance.
    queryClient.setQueryData(queryKeys.legalAcceptance(userId), {
      version: currentVersion,
      acceptedAt: new Date().toISOString(),
    });
  }, [currentVersion, queryClient, userId]);

  return (
    <LegalGateContext.Provider value={{ status, currentVersion, acceptLegal }}>
      {children}
    </LegalGateContext.Provider>
  );
};

export const useLegalGate = () => {
  const context = useContext(LegalGateContext);
  if (!context) {
    throw new Error("useLegalGate must be used within LegalGateProvider");
  }
  return context;
};
